import { useEffect } from 'react';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';

import useToggle from '@/utils/hooks/utils/useToggle';
import useSubscription from '@/utils/hooks/swr/pricing/useSubscription';
import useRouterQuery from '@/utils/hooks/utils/useRouterQuery';

//type
import type { ActivePlanType } from '@/types/pricingTypes/subscriptionType';
import type { URLStepType } from '@/components/pages/settings/subscription-invoices/PageContent/types';

//lib
import { CHANGE_PAYMENT_METHOD } from '@/lib/settings/subscriptionInvoices/steps';

//components
import Button from '@/components/base/Button';
import { ChangePaymentModal } from '@/components/pages/settings/subscription-invoices/UserBilling/ChangePaymentModal';

function UpdatePaymentMethod() {
    const { t } = useTranslation('settings');
    const { subscription } = useSubscription();
    const router = useRouter();
    const { step } = router.query;
    const urlStep = step as URLStepType;
    const { clearUrlQueriesWithoutReload } = useRouterQuery();
    const { isOpen, toggle } = useToggle({
        initial: Boolean(urlStep === CHANGE_PAYMENT_METHOD)
    });

    // when this component is rendered, the user always has a subscription
    const currentSubscription = subscription as ActivePlanType;

    useEffect(() => {
        if (!urlStep || urlStep !== CHANGE_PAYMENT_METHOD) {
            return;
        }
        if (!isOpen) {
            toggle();
        }

        clearUrlQueriesWithoutReload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlStep]);

    if (!currentSubscription.transaction_id) {
        return null;
    }

    return (
        <div className="w-full">
            <Button
                data-testid="new-pricing-update-payment-method"
                className="w-full"
                variant="secondary"
                onClick={toggle}>
                {t('subscription_invoices_page.change_payment_method')}
            </Button>
            <ChangePaymentModal isOpen={isOpen} closeModal={toggle} />
        </div>
    );
}

function UpdateBillingAddress() {
    const { subscription } = useSubscription();
    const { t } = useTranslation('settings');

    // when this component is rendered, the user always has a subscription
    const currentSubscription = subscription as ActivePlanType;

    if (!currentSubscription.update_billing_address_url) {
        return null;
    }

    return (
        <Link
            data-testid="new-pricing-update-billing-address"
            className="text-xs text-icoBlue underline"
            href={currentSubscription.update_billing_address_url}
            target="_blank"
            rel="noopener noreferrer">
            {t('subscription_invoices_page.change_billing_address')}
        </Link>
    );
}

export function BillingActions() {
    const { subscription } = useSubscription();

    // when this component is rendered, the user always has a subscription
    const currentSubscription = subscription as ActivePlanType;

    if (!currentSubscription.transaction_id && !currentSubscription.update_billing_address_url) {
        return null;
    }

    return (
        <div className="flex flex-col items-center gap-2">
            <UpdatePaymentMethod />
            <UpdateBillingAddress />
        </div>
    );
}
